import {injectable} from 'inversify'
import i18next from 'i18next'
import {action, computed, makeObservable, observable, reaction, runInAction} from 'mobx'
import {EpaySummary, getEpaySummary, getOverviewPOSSummary, PaymentsStatuses} from '@/stores'
import {
    TRangePickerContainerStore,
    TSelectContainerStore,
    TTransactionSummaryContainerStore
} from '@/components/containers'
import {Currencies, PaymentsType} from '@/constants'
import {DEFAULT_CURRENCY} from '@/constants/currencies'
import {LoadingState} from '@/types'
import {openErrorNotification} from '@/utils'
import {filterECOMByStatuses, filterPOSByStatuses} from '@/stores/reports/services/utills'
import {IPaymentsTransactionsFilterStore} from '~/code/pages/Overview/components/OveriviewPaymentsFilter'
import {hasPermissions} from '../../auth/services'
import {PermissionsMap} from '../../auth/constants/permissions-map'

@injectable()
export class PaymentsTransactionSummaryStore implements TTransactionSummaryContainerStore {
    private readonly _rangePickerStore: TRangePickerContainerStore
    private readonly _currencySelectStore: TSelectContainerStore
    private paymentsTransactionsFilterStore: IPaymentsTransactionsFilterStore

    constructor(
        protected _name: string,
        rangePickerStore: TRangePickerContainerStore,
        currencySelectStore: TSelectContainerStore,
        paymentsTransactionsFilterStore: IPaymentsTransactionsFilterStore
    ) {
        this._rangePickerStore = rangePickerStore
        this._currencySelectStore = currencySelectStore
        this.paymentsTransactionsFilterStore = paymentsTransactionsFilterStore

        makeObservable(this, {
            loadingState: observable,
            posSummary: observable,
            ecomSummary: observable,

            summaryByStatus: computed,
            currSummaryRequest: computed,
            prevDates: computed,
            currency: computed,
            loadSummary: action.bound
        })

        this._currencySelectStore.setValue(DEFAULT_CURRENCY)
        this.loadSummary()

        reaction(
            () => ({
                request: this.currSummaryRequest,
                transactionStore: this.paymentsTransactionsFilterStore.transactionStore,
                ecomStores: this.paymentsTransactionsFilterStore.ecomStores,
                posStores: this.paymentsTransactionsFilterStore.posStores
            }),
            ({request, ecomStores, posStores}) => {
                // Wait for stores to be loaded and currency to be set
                if (request.currency && ecomStores !== undefined && posStores !== undefined) {
                    this.loadSummary()
                }
            }
        )
    }

    loadingState = LoadingState.IDLE
    posSummary: EpaySummary = {currency: null, data: []}
    ecomSummary: EpaySummary = {currency: null, data: []}

    get summaryByStatus() {
        const ecom = filterECOMByStatuses(this.ecomSummary.data)
        const pos = filterPOSByStatuses(this.posSummary.data)

        // Check if both data sources are empty
        const hasNoData = this.ecomSummary.data.length === 0 && this.posSummary.data.length === 0

        // Merge ECOM and POS data by summing amounts and counts for each status
        return PaymentsStatuses.map((status) => {
            const ecomItem = ecom.find((item) => item.status === status)
            const posItem = pos.find((item) => item.status === status)

            return {
                status,
                amount: (ecomItem?.amount ?? 0) + (posItem?.amount ?? 0),
                // Return undefined for count when there's no data available
                count: hasNoData ? undefined : (ecomItem?.count ?? 0) + (posItem?.count ?? 0)
            }
        })
    }

    get currSummaryRequest() {
        return {
            from: this.paymentsTransactionsFilterStore.startDate.format(),
            to: this.paymentsTransactionsFilterStore.endDate.format(),
            currency: this.paymentsTransactionsFilterStore.currency,
        }
    }

    get prevDates() {
        return {
            from: this._rangePickerStore.prevDates.startDate,
            to: this._rangePickerStore.prevDates.endDate
        }
    }

    get currency() {
        return this.paymentsTransactionsFilterStore.currency as Currencies
    }

    private get hasPosPermissions() {
        return hasPermissions([PermissionsMap.pos_payments.read, PermissionsMap.pos_payments.full])
    }

    private get hasEcomPermissions() {
        return hasPermissions([
            PermissionsMap.online_payments.read,
            PermissionsMap.online_payments.full
        ])
    }

    private get hasEcomStores(): boolean {
        return this.paymentsTransactionsFilterStore.ecomStores != null && (this.paymentsTransactionsFilterStore.ecomStores?.length ?? 0) > 0
    }

    private get hasPosStores(): boolean {
        return this.paymentsTransactionsFilterStore.posStores != null && (this.paymentsTransactionsFilterStore.posStores?.length ?? 0) > 0
    }

    // AbortController for in-flight POS requests — aborted when a newer request starts
    private _posAbortController: AbortController | null = null

    private clearAllData() {
        runInAction(() => {
            this.posSummary = {currency: null, data: []}
            this.ecomSummary = {currency: null, data: []}
        })
    }

    private async fetchBothSummaries(signal: AbortSignal) {
        // Capture request parameters before async operations to ensure consistency
        const request = this.currSummaryRequest
        const requestCurrency = this.paymentsTransactionsFilterStore.currency

        // Build requests with store filters
        const posRequest = {
            ...request,
            ...this.paymentsTransactionsFilterStore.posStoreValue
        }
        const ecomRequest = {
            ...request,
            storeId: this.paymentsTransactionsFilterStore.ecomStoreValue
        }

        const [pos, ecom] = await Promise.all([
            getOverviewPOSSummary(posRequest, signal),
            getEpaySummary(ecomRequest)
        ])

        if (pos.error) {
            throw new Error(
                pos.error.message ||
                    i18next.t('PaymentsTransactionSummary.errors.failedToFetchPosSummary')
            )
        }
        if (ecom.error) {
            throw new Error(
                ecom.error.message ||
                    i18next.t('PaymentsTransactionSummary.errors.failedToFetchEcomSummary')
            )
        }

        // Verify currency hasn't changed during the request
        if (requestCurrency !== this.paymentsTransactionsFilterStore.currency) {
            // Currency changed during request, discard stale results
            return
        }

        runInAction(() => {
            this.posSummary = {currency: requestCurrency, data: pos.result.data}
            this.ecomSummary = ecom.result
        })
    }

    private async fetchEcomOnly() {
        // Capture request parameters before async operations to ensure consistency
        const request = this.currSummaryRequest
        const requestCurrency = this.paymentsTransactionsFilterStore.currency

        // Build request with storeId filter
        const ecomRequest = {
            ...request,
            storeId: this.paymentsTransactionsFilterStore.ecomStoreValue
        }

        const {result, error} = await getEpaySummary(ecomRequest)
        if (error) {
            throw new Error(
                error.message ||
                    i18next.t('PaymentsTransactionSummary.errors.failedToFetchEcomSummary')
            )
        }

        // Verify currency hasn't changed during the request
        if (requestCurrency !== this.paymentsTransactionsFilterStore.currency) {
            // Currency changed during request, discard stale results
            return
        }

        runInAction(() => {
            this.ecomSummary = result
            this.posSummary = {currency: null, data: []}
        })
    }

    private async fetchPosOnly(signal: AbortSignal) {
        // Capture request parameters before async operations to ensure consistency
        const request = this.currSummaryRequest
        const requestCurrency = this.paymentsTransactionsFilterStore.currency

        // Build request with mids filter (posStoreValue contains mids)
        const posRequest = {
            ...request,
            ...this.paymentsTransactionsFilterStore.posStoreValue
        }

        const {result, error} = await getOverviewPOSSummary(posRequest, signal)
        if (error) {
            throw new Error(
                error.message ||
                    i18next.t('PaymentsTransactionSummary.errors.failedToFetchPosSummary')
            )
        }

        // Verify currency hasn't changed during the request
        if (requestCurrency !== this.paymentsTransactionsFilterStore.currency) {
            // Currency changed during request, discard stale results
            return
        }

        runInAction(() => {
            this.posSummary = {currency: requestCurrency, data: result.data} as EpaySummary
            this.ecomSummary = {currency: null, data: []}
        })
    }

    private async handleAllPaymentsType(signal: AbortSignal) {
        const canFetchEcom = this.hasEcomStores && this.hasEcomPermissions
        const canFetchPos = this.hasPosStores && this.hasPosPermissions

        // No stores or permissions available
        if (!canFetchEcom && !canFetchPos) {
            this.clearAllData()
            return
        }

        // Only POS stores available
        if (!this.hasEcomStores && canFetchPos) {
            await this.fetchPosOnly(signal)
            return
        }

        // Only ECOM stores available
        if (!this.hasPosStores && canFetchEcom) {
            await this.fetchEcomOnly()
            return
        }

        // Both store types available - fetch both
        if (canFetchEcom && canFetchPos) {
            await this.fetchBothSummaries(signal)
            return
        }

        // Fallback: fetch whichever store type is available
        if (canFetchEcom) {
            await this.fetchEcomOnly()
        } else if (canFetchPos) {
            await this.fetchPosOnly(signal)
        }
    }

    private async handleEcomPaymentsType() {
        if (this.hasEcomPermissions) {
            await this.fetchEcomOnly()
        } else {
            this.clearAllData()
        }
    }

    private async handlePosPaymentsType(signal: AbortSignal) {
        if (this.hasPosPermissions) {
            await this.fetchPosOnly(signal)
        } else {
            this.clearAllData()
        }
    }

    public async loadSummary() {
        // Abort any in-flight POS request before starting a new one
        this._posAbortController?.abort()
        this._posAbortController = new AbortController()
        const signal = this._posAbortController.signal

        runInAction(() => {
            this.loadingState = LoadingState.LOADING
        })

        const paymentsType = this.paymentsTransactionsFilterStore.paymentsType

        try {
            switch (paymentsType) {
                case PaymentsType.all:
                    await this.handleAllPaymentsType(signal)
                    break
                case PaymentsType.ecom:
                    await this.handleEcomPaymentsType()
                    break
                case PaymentsType.pos:
                    await this.handlePosPaymentsType(signal)
                    break
            }

            runInAction(() => {
                this.loadingState = LoadingState.DONE
            })
        } catch (error) {
            // Silently ignore aborted requests — a newer request is already in flight
            if (error?.name === 'AbortError') return

            runInAction(() => {
                this.loadingState = LoadingState.FAILED
            })
            // Clear data on error to prevent stale data from being displayed
            this.clearAllData()
            return openErrorNotification(error.message)
        }
    }
}
